import {
  Box,
  Button,
  Chip,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import strings from "global/constants/StringConstants";
import {
  debounceEventHandler,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { useEffect, useState } from "react";
import SearchIcon from "assets/icons1/search_icon.svg";
import settingsStyles from "../Settings.styles";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import EditDetails from "assets/icons1/EditDetailsIcon.svg";
import {
  fetchResources,
  fetchRoles,
  fetchRolesCount,
  fetchSearchCount,
  fetchSearchParams,
} from "../SettingsService";
import { cloneDeep } from "lodash";
import { RoleFields } from "models/interfaces";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import viewpaths from "global/constants/ViewPathConstants";
import { AddUpdateRoles } from "./AddUpdateRoles";
import { initialRoleData } from "./RoleManagementHelpers";
import { RouterLink } from "components/router-link";
import KeyboardBackspace from "@mui/icons-material/KeyboardBackspace";

const tableHeader = [
  {
    name: "Role Name",
    field: "name",
  },
  {
    name: "Resources And Permissions",
    field: "resources",
  },
];

const RoleManagement = () => {
  useTitle(title.ROLE_MANAGEMENT);
  const theme = useTheme();
  const classes = settingsStyles(theme);
  const [roles, setRoles] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [resources, setResources] = useState<any>([]);
  const [buttonClick, setButtonClick] = useState<string>(strings.rolesTable);
  const [roleData, setRoleData] = useState<any>(initialRoleData);

  const [createRole, setCreateRole] = useState<{
    visible: boolean;
    metadata: RoleFields;
  }>({
    visible: false,
    metadata: {
      name: "",
      resources: [
        {
          name: "",
          path: "",
          permissions: [],
        },
      ],
    },
  });

  useEffect(() => {
    if (isTruthy(searchTerm)) {
      getSearchParamsData(searchTerm, pageNumber);
    } else {
      getRolesData();
    }
  }, [pageNumber, rowsPerPage, searchTerm]);

  const getRolesData = async () => {
    try {
      setLoading(true);
      const [roles, count, resources] = await Promise.all([
        fetchRoles(rowsPerPage, pageNumber),
        fetchRolesCount(),
        fetchResources(),
      ]);
      const finalSearchData = convertDataToTableFormat(roles);
      setRoles(finalSearchData);
      setCount(count);
      setResources(resources);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const getSearchParamsData = async (search: string, page: number) => {
    try {
      let count: number;
      let response: any;
      setLoading(true);
      [count, response] = await Promise.all([
        fetchSearchCount(strings.roleManagement, search),
        fetchSearchParams(strings.roleManagement, page, rowsPerPage, search),
      ]);
      const tableData = convertDataToTableFormat(response);
      setRoles([...tableData]);
      setCount(count);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const convertDataToTableFormat = (data: any, rolesData?: any) => {
    return data?.map((item: any, index: number) => {
      return {
        name:
          item.id !== 1 && item.id !== 2 ? (
            <Link
              color="text.primary"
              variant="subtitle2"
              component={"button"}
              onClick={() => {
                handleEdit(item);
              }}
            >
              {item.name}
            </Link>
          ) : (
            <Typography color="text.primary" variant="subtitle2">
              {item.name}
            </Typography>
          ),
        resources: item?.resources?.map((res: any) => {
          return (
            <>
              <Stack
                direction="row"
                alignItems="center"
                alignContent="center"
                spacing={1}
                mt={1}
              >
                <Typography variant="body2">{res.name}:</Typography>
                {res?.permissions?.map((i: any) => (
                  <Chip
                    key={index}
                    label={i}
                    sx={classes.chip}
                    variant="filled"
                  />
                ))}
              </Stack>
            </>
          );
        }),
        action: item.id !== 1 && item.id !== 2 && (
          <Tooltip title="Edit Role" placement="top" arrow>
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setCreateRole((prevState: any) => {
                  return {
                    ...prevState,
                    visible: true,
                    metadata: {
                      ...cloneDeep(item),
                    },
                  };
                });
              }}
            >
              <img src={EditDetails} />
            </Box>
          </Tooltip>
        ),
      };
    });
  };

  const handleSearchParams = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value) {
      setSearchTerm(event.target.value.trim());
      setPageNumber(1);
    } else {
      setSearchTerm("");
      setPageNumber(1);
    }
  };

  const handleRowsPerPage = (rowPerPage: number) => {
    setRowsPerPage(rowPerPage);
    setPageNumber(1);
  };

  const handlePageChange = (event: any, pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const handleEdit = (rowData: any) => {
    setButtonClick(strings.editRole);
    setRoleData(rowData);
  };

  const handleRoleCreator = () => {
    setButtonClick(strings.createRole);
    setRoleData(initialRoleData);
  };

  const getPageHeader = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="roles_search_box"
            placeholder="Search roles"
            fullWidth
            onChange={debounceEventHandler(
              handleSearchParams,
              strings.SEARCH_TIME_OUT
            )}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
        </Stack>
      </CenteredCardLayout>
    );
  };

  const editModeRolePage = () => {
    switch (buttonClick) {
      case strings.createRole:
      case strings.editRole:
        return (
          <Stack spacing={2} p={2.5}>
            <AddUpdateRoles
              name={buttonClick}
              setButtonClick={setButtonClick}
              rowData={roleData}
              fetchRolesHandler={getRolesData}
              resources={resources}
            />
          </Stack>
        );
      case strings.rolesTable:
      default:
        return (
          <Box>
            <CustomTable
              headers={tableHeader}
              rows={roles}
              rowsPerPage={rowsPerPage}
              count={count}
              page={pageNumber}
              onRowsPerPageChange={handleRowsPerPage}
              onPageChange={handlePageChange}
              isLoading={loading}
            />
          </Box>
        );
    }
  };

  const getRolesPage = () => {
    const notRolesTable = buttonClick !== strings.rolesTable;
    return (
      <Stack direction="column" spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center">
            <Link
              color="text.primary"
              href={viewpaths.rolesViewPath}
              sx={{
                alignItems: "center",
                display: "inline-flex",
                cursor: notRolesTable ? "pointer" : "normal",
              }}
              onClick={() =>
                notRolesTable && setButtonClick(strings.rolesTable)
              }
              underline="hover"
              component={RouterLink}
            >
              {notRolesTable && (
                <KeyboardBackspace
                  htmlColor="#475569"
                  sx={{ cursor: "pointer", pr: 1 }}
                />
              )}
              <Typography
                variant={buttonClick === strings.rolesTable ? "h5" : "body1"}
                color="text.primary"
                fontWeight={700}
              >
                {buttonClick}
              </Typography>
            </Link>
          </Stack>
          {buttonClick === strings.rolesTable && (
            <Box>
              <Button
                id="send_invite"
                variant="contained"
                size="small"
                onClick={handleRoleCreator}
                disabled={loading}
              >
                Create Role
              </Button>
            </Box>
          )}
        </Stack>
        {buttonClick === strings.rolesTable && getPageHeader()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          {editModeRolePage()}
        </CenteredCardLayout>
      </Stack>
    );
  };

  return (
    <>
      <CenteredAuthenticatedLayout>
        {getRolesPage()}
      </CenteredAuthenticatedLayout>
    </>
  );
};

export default RoleManagement;
